import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, finalize, share } from 'rxjs';

import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import {
  CreateSolverParameterDto,
  SolverParameterDto,
  SolverParameterListDto,
  UpdateSolverParameterDto
} from '@core/models/parameter.model';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

@Injectable({
  providedIn: 'root'
})
export class ParamsService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly paramsInFlight = new Map<string, Observable<SolverParameterListDto>>();

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  public addParams$(param: CreateSolverParameterDto): Observable<SolverParameterDto> {
    // TODO: Remove hardcoded values when backend is ready
    const body = {
      name: param.name,
      description: param.description,
      modelParameterId: 1,
      orchestratorParameterId: 1,
      hiddenModelParameterId: 1,
      hiddenOrchestratorParameterId: 1
    };
    const url = this.buildUrl(GbeEndPointV4.SOLVERS) + `/${param.solverId}/parameters`;
    return this.http.post<SolverParameterDto>(url, body);
  }

  public getParamsBySolverId$(
    solverId: number,
    currentPage: number = 1,
    pageSize: number = 25
  ): Observable<SolverParameterListDto> {
    // The plan overview and the findings context both look up the plan's solver as the plan
    // lands, so a lookup already in flight is joined rather than sent again. Nothing is kept
    // once it answers: parameter sets are created and deleted elsewhere.
    const key = `${solverId}/${currentPage}/${pageSize}`;
    const inFlight = this.paramsInFlight.get(key);
    if (inFlight) return inFlight;
    const url = this.buildUrl(GbeEndPointV4.SOLVERS) + `/${solverId}/parameters`;
    const request$ = this.http
      .get<SolverParameterListDto>(url, {
        params: {
          page: currentPage,
          take: pageSize,
          solverId: solverId
        }
      })
      .pipe(
        finalize(() => {
          if (this.paramsInFlight.get(key) === request$) this.paramsInFlight.delete(key);
        }),
        share()
      );
    this.paramsInFlight.set(key, request$);
    return request$;
  }

  public getParamById$(paramId: number, solverId: number): Observable<SolverParameterDto> {
    const url = this.buildUrl(GbeEndPointV4.SOLVERS) + `/${solverId}/parameters/${paramId}`;
    return this.http.get<SolverParameterDto>(url, {
      params: {
        paramId: paramId,
        solverId: solverId
      }
    });
  }

  public updateParams$(
    paramId: number,
    solverId: number,
    param: UpdateSolverParameterDto
  ): Observable<SolverParameterDto> {
    const url = this.buildUrl(GbeEndPointV4.SOLVERS) + `/${solverId}/parameters/${paramId}`;
    const body = {
      name: param.name,
      description: param.description
    };
    return this.http.put<SolverParameterDto>(url, body, {
      params: {
        paramId: paramId,
        solverId: solverId
      }
    });
  }

  public deleteParam$(paramId: number, solverId: number): Observable<void> {
    const url = this.buildUrl(GbeEndPointV4.SOLVERS) + `/${solverId}/parameters/${paramId}`;
    return this.http.delete<void>(url, {
      params: {
        paramId: paramId
      }
    });
  }
}
